import { useCallback, useEffect, useRef, useState, type ComponentType } from 'react'
import { Capacitor } from '@capacitor/core'
import { Camera, CameraDirection, CameraResultType, CameraSource } from '@capacitor/camera'
import { client } from '../lib/api'
import { ensureCameraPermission } from '../lib/nativePermissions'

function nativeCameraAvailable(): boolean {
  return Capacitor.isPluginAvailable('Camera')
}

type Props = {
  status?: string
  notify: (msg: string) => void
  onVerified: () => void
  minPhotos?: number
}

type ProviderInfo = {
  provider: 'rekognition' | 'facepp' | 'sightengine' | 'demo'
  region: string
  liveness: boolean
  message: string
}

type LivenessSession = {
  sessionId: string
  region: string
  credentials: {
    accessKeyId: string
    secretAccessKey: string
    sessionToken: string
    expiration: string
  }
}

type LivenessProps = {
  sessionId: string
  region: string
  credentials: LivenessSession['credentials']
  onComplete: () => void
  onError: (message: string) => void
}

export function SelfieVerification({ status, notify, onVerified, minPhotos = 1 }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [busy, setBusy] = useState(false)
  const [cameraBusy, setCameraBusy] = useState(false)
  const [caseStatus, setCaseStatus] = useState(status ?? 'none')
  const [provider, setProvider] = useState<ProviderInfo | null>(null)
  const [liveness, setLiveness] = useState<LivenessSession | null>(null)
  const [LivenessComp, setLivenessComp] = useState<ComponentType<LivenessProps> | null>(null)

  useEffect(() => {
    client
      .verificationStatus()
      .then((r) => setCaseStatus(r.verificationStatus))
      .catch(() => undefined)
    client
      .verificationProvider()
      .then(setProvider)
      .catch(() => setProvider({ provider: 'demo', region: 'us-east-1', liveness: false, message: '' }))
  }, [])

  const stopCamera = useCallback(() => {
    stream?.getTracks().forEach((t) => t.stop())
    setStream(null)
  }, [stream])

  useEffect(() => () => stopCamera(), [stopCamera])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !stream) return
    video.srcObject = stream
    void video.play().catch(() => undefined)
  }, [stream])

  const applyResult = (res: { status: string; message: string }) => {
    if (res.status === 'approved') {
      setCaseStatus('verified')
      notify(res.message)
      onVerified()
    } else if (res.status === 'rejected') {
      setCaseStatus('rejected')
      setPreview(null)
      setBlob(null)
      setLiveness(null)
      notify(res.message)
    } else {
      setCaseStatus('pending')
      notify(res.message)
      onVerified()
    }
  }

  const startLiveness = async () => {
    setCameraBusy(true)
    try {
      const allowed = await ensureCameraPermission()
      if (!allowed) {
        notify('Allow camera access in Settings to verify with a selfie')
        return
      }
      const [{ FaceLivenessCheck }, session] = await Promise.all([
        import('./FaceLivenessCheck'),
        client.createLivenessSession(),
      ])
      setLivenessComp(() => FaceLivenessCheck)
      setLiveness({
        sessionId: session.sessionId,
        region: session.region,
        credentials: session.credentials,
      })
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setCameraBusy(false)
    }
  }

  const startCamera = async () => {
    setCameraBusy(true)
    try {
      const allowed = await ensureCameraPermission()
      if (!allowed) {
        notify('Allow camera access in Settings to verify with a selfie')
        return
      }

      if (nativeCameraAvailable()) {
        const photo = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Uri,
          source: CameraSource.Camera,
          direction: CameraDirection.Front,
        })
        if (!photo.webPath) throw new Error('No photo captured')
        const res = await fetch(photo.webPath)
        const captured = await res.blob()
        stopCamera()
        setBlob(captured)
        setPreview(URL.createObjectURL(captured))
        return
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        notify('Camera is not available in this browser')
        return
      }

      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      })
      setPreview(null)
      setBlob(null)
      setStream(media)
    } catch (e) {
      const msg = (e as Error).message || 'Could not open the camera'
      notify(
        msg.includes('denied') || msg.includes('Permission')
          ? 'Allow camera access in Settings to verify with a selfie'
          : `Could not open the camera — ${msg}`,
      )
    } finally {
      setCameraBusy(false)
    }
  }

  const capture = () => {
    const video = videoRef.current
    if (!video) return
    const canvas = document.createElement('canvas')
    const size = Math.min(video.videoWidth || 640, video.videoHeight || 640, 720)
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const sx = Math.max(0, (video.videoWidth - size) / 2)
    const sy = Math.max(0, (video.videoHeight - size) / 2)
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size)
    canvas.toBlob(
      (b) => {
        if (!b) return
        setBlob(b)
        setPreview(URL.createObjectURL(b))
        stopCamera()
      },
      'image/jpeg',
      0.9,
    )
  }

  const submit = async () => {
    if (!blob) return
    setBusy(true)
    try {
      const res = await client.submitSelfie(blob)
      applyResult(res)
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const onLivenessComplete = async () => {
    if (!liveness) return
    setBusy(true)
    try {
      const res = await client.completeLivenessSelfie(liveness.sessionId)
      applyResult(res)
    } catch (e) {
      notify((e as Error).message)
      setLiveness(null)
    } finally {
      setBusy(false)
    }
  }

  if (caseStatus === 'verified') {
    return (
      <div className="selfie-card verified">
        <strong>✓ Verified member</strong>
        <p>Your live selfie matched your profile photos. The verified badge appears on your profile.</p>
      </div>
    )
  }

  if (caseStatus === 'pending') {
    return (
      <div className="selfie-card">
        <strong>Verification pending</strong>
        <p>Your selfie is in review. This appears in Account Standing — never as a silent restriction.</p>
      </div>
    )
  }

  const useLiveness = provider?.liveness === true

  return (
    <div className="selfie-card">
      <strong>Face Check</strong>
      <p className="page-sub">
        {useLiveness
          ? 'Short video selfie — we confirm you are live, then match your face to your profile photos. No legal ID, ever.'
          : 'Take a live selfie — matched against the photos on your profile. No legal ID, ever.'}{' '}
        {minPhotos > 1
          ? `You need at least ${minPhotos} profile photos uploaded first.`
          : 'Upload at least one clear photo of your face first.'}
      </p>
      {provider?.message && <p className="field-hint">{provider.message}</p>}
      {caseStatus === 'rejected' && (
        <p className="page-sub" style={{ color: 'var(--danger)' }}>
          Your last check didn&apos;t pass. Use good lighting, look at the camera, and make sure your profile photos
          clearly show your face.
        </p>
      )}

      {useLiveness && liveness && LivenessComp && (
        <div className="selfie-liveness">
          <LivenessComp
            sessionId={liveness.sessionId}
            region={liveness.region}
            credentials={liveness.credentials}
            onComplete={() => void onLivenessComplete()}
            onError={(msg) => {
              notify(msg)
              setLiveness(null)
            }}
          />
          {busy && <p className="field-hint">Checking results…</p>}
          <button className="button secondary" type="button" disabled={busy} onClick={() => setLiveness(null)}>
            Cancel
          </button>
        </div>
      )}

      {useLiveness && !liveness && (
        <button className="button primary" type="button" disabled={cameraBusy || busy} onClick={() => void startLiveness()}>
          {cameraBusy ? 'Starting Face Check…' : 'Start Face Check'}
        </button>
      )}

      {!useLiveness && (
        <>
          {!preview && !stream && (
            <button className="button primary" type="button" disabled={cameraBusy} onClick={() => void startCamera()}>
              {cameraBusy ? 'Opening camera…' : nativeCameraAvailable() ? 'Take selfie' : 'Open camera'}
            </button>
          )}

          {stream && (
            <div className="selfie-stage">
              <video ref={videoRef} playsInline muted autoPlay className="selfie-video" />
              <button className="button primary" type="button" onClick={capture}>
                Capture selfie
              </button>
            </div>
          )}

          {preview && (
            <div className="selfie-stage">
              <img src={preview} alt="Selfie preview" className="selfie-preview" />
              <div className="modal-actions">
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => void startCamera()}
                  disabled={busy || cameraBusy}
                >
                  Retake
                </button>
                <button className="button primary" type="button" onClick={() => void submit()} disabled={busy}>
                  {busy ? 'Submitting…' : 'Submit for review'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
